/**
 * QuantumFlowSync.AI - Role Detail Page
 * View role details and manage permissions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Shield,
  Users,
  Key,
  Edit,
  Trash2,
  Plus,
  Search,
  Lock,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";

export default function RoleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, hasTier } = useAuth();
  
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [searchPermission, setSearchPermission] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    display_name: '',
    description: ''
  });

  useEffect(() => {
    loadRoleDetails();
  }, [id]);

  const loadRoleDetails = async () => {
    try {
      setLoading(true);
      const [roleRes, permsRes, empsRes] = await Promise.all([
        axios.get(`${API}/admin/roles/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }),
        axios.get(`${API}/admin/roles/${id}/permissions`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }),
        axios.get(`${API}/admin/employees?role_id=${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
      ]);

      setRole(roleRes.data);
      setPermissions(permsRes.data.permissions || []);
      setEmployees(empsRes.data.employees || []);
      setEditForm({
        display_name: roleRes.data.display_name || roleRes.data.name,
        description: roleRes.data.description || ''
      });
    } catch (error) {
      console.error('Failed to load role details:', error);
      toast.error('Failed to load role details');
    } finally {
      setLoading(false);
    }
  };

  const loadAllPermissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/permissions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setAllPermissions(response.data.permissions || []);
      setSelectedPermissions(permissions.map(p => p.id));
    } catch (error) {
      console.error('Failed to load permissions:', error);
      toast.error('Failed to load permissions');
    }
  };

  const handleEditRole = async () => {
    try {
      await axios.put(
        `${API}/admin/roles/${id}`,
        editForm,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      toast.success('Role updated successfully');
      setShowEditDialog(false);
      loadRoleDetails();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await axios.post(
        `${API}/admin/roles/${id}/permissions`,
        { permission_ids: selectedPermissions },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      toast.success('Permissions updated successfully');
      setShowPermissionsDialog(false);
      loadRoleDetails();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleDeleteRole = async () => {
    if (!confirm(`Are you sure you want to delete the role "${role.display_name || role.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/roles/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });

      toast.success('Role deleted successfully');
      navigate('/admin/roles');
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const filteredPermissions = allPermissions.filter(p =>
    p.resource.toLowerCase().includes(searchPermission.toLowerCase()) ||
    p.action.toLowerCase().includes(searchPermission.toLowerCase()) ||
    p.description.toLowerCase().includes(searchPermission.toLowerCase())
  );

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <h2 className="text-2xl font-bold mb-2">Role Not Found</h2>
          <Button onClick={() => navigate('/admin/roles')}>
            Back to Roles
          </Button>
        </div>
      </div>
    );
  }

  const canEdit = hasTier(1);
  const canDelete = hasTier(1) && !role.is_system;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/roles')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{role.display_name || role.name}</h1>
              <Badge variant="outline">Tier {role.tier}</Badge>
              {role.is_system && (
                <Badge variant="secondary">
                  <Lock className="w-3 h-3 mr-1" />
                  System Role
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">{role.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDeleteRole}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Stats Cards */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Permissions</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.length}</div>
            <p className="text-xs text-muted-foreground">
              Assigned permissions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Employees</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{employees.length}</div>
            <p className="text-xs text-muted-foreground">
              With this role
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hierarchy</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Tier {role.tier}</div>
            <p className="text-xs text-muted-foreground">
              {role.parent_role ? `Inherits from ${role.parent_role.name}` : 'Top-level role'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Permissions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Permissions</CardTitle>
              <CardDescription>
                Permissions granted to this role
              </CardDescription>
            </div>
            {canEdit && (
              <Button
                onClick={() => {
                  loadAllPermissions();
                  setShowPermissionsDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Permissions
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {permissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Key className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No permissions assigned</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedPermissions).map(([resource, perms]) => (
                <div key={resource}>
                  <h3 className="font-semibold text-lg mb-3 capitalize">{resource}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {perms.map((perm) => (
                      <div
                        key={perm.id}
                        className="flex items-center gap-2 p-3 rounded-lg border bg-card"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm truncate">
                            {perm.resource}:{perm.scope}:{perm.action}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {perm.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employees */}
      <Card>
        <CardHeader>
          <CardTitle>Employees with this Role</CardTitle>
          <CardDescription>
            {employees.length} employee{employees.length !== 1 ? 's' : ''} assigned
          </CardDescription>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No employees with this role</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => (
                  <TableRow
                    key={emp.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/admin/employees/${emp.id}`)}
                  >
                    <TableCell className="font-medium">{emp.name}</TableCell>
                    <TableCell>{emp.email}</TableCell>
                    <TableCell className="capitalize">{emp.department}</TableCell>
                    <TableCell>
                      <Badge variant={emp.status === 'active' ? 'success' : 'secondary'}>
                        {emp.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>
              Update role information. System roles have limited editability.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                placeholder="e.g., Super Administrator"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Describe the role's purpose and responsibilities"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditRole}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Select permissions to assign to this role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search permissions..."
                value={searchPermission}
                onChange={(e) => setSearchPermission(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              {filteredPermissions.map((perm) => (
                <div
                  key={perm.id}
                  className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selectedPermissions.includes(perm.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedPermissions([...selectedPermissions, perm.id]);
                      } else {
                        setSelectedPermissions(selectedPermissions.filter(id => id !== perm.id));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <p className="font-mono text-sm">
                      {perm.resource}:{perm.scope}:{perm.action}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {perm.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedPermissions.length} permission{selectedPermissions.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePermissions}>
              Update Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
